import { loginInputSchema, sessionSchema } from '@canopee/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { apiRequest } from '../../api/client.js';
import { sessionQueryKey } from '../../auth/session.js';
import styles from './login-screen.module.css';

export function LoginScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const login = useMutation({
    mutationFn: (input: unknown) =>
      apiRequest('/api/v1/auth/login', sessionSchema, { method: 'POST', body: input }),
    onSuccess: async (session) => {
      queryClient.setQueryData(sessionQueryKey, session);
      await navigate('/');
    },
  });

  function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>) {
    event.preventDefault();
    setFormError(null);
    const data = new FormData(event.currentTarget);
    const parsed = loginInputSchema.safeParse({
      email: data.get('email'),
      password: data.get('password'),
    });
    if (!parsed.success) {
      setFormError('Vérifiez l’email et le mot de passe saisis.');
      return;
    }
    login.mutate(parsed.data);
  }

  const errorMessage = login.isError ? login.error.message : formError;
  return (
    <main className={styles.page}>
      <form className={styles.card} onSubmit={submit}>
        <div>
          <p className={styles.eyebrow}>Canopée</p>
          <h1>Connexion</h1>
          <p>Accédez à votre espace de gestion.</p>
        </div>
        <label className={styles.field}>
          <span>Email</span>
          <input autoComplete="username" name="email" required type="email" />
        </label>
        <label className={styles.field}>
          <span>Mot de passe</span>
          <input autoComplete="current-password" name="password" required type="password" />
        </label>
        {errorMessage === null ? null : (
          <p aria-live="polite" className={styles.error} role="alert">
            {errorMessage}
          </p>
        )}
        <button className={styles.submit} disabled={login.isPending} type="submit">
          {login.isPending ? 'Connexion…' : 'Se connecter'}
        </button>
        <Link className={styles.link} to="/forgot-password">
          Mot de passe oublié ?
        </Link>
      </form>
    </main>
  );
}
